import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Field, withTypes } from 'react-final-form';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { Table } from 'reactstrap';
import { ProfileQueryKeys } from 'src/modules/myProfile/src/profileQueries';
import { useKnowledgeTestDispatch, useKnowledgeTestState } from '../../contexts/KnowledgeTestContext';
import { useDict } from '../../contexts/LanguageContext';
import ROUTE_NAME from '../../routes/RouteName';
import { appendLocale } from '../../utils/helper';
import CustomRadioButton from '../form/CustomRadioButton';
import Modal from './Modal';
import { getKnowledgeTestQuestions, postSubmitKnowledgeTest } from './src/knowledgeTestApis';

const KnowledgeTestModal = () => {
    const dict = useDict();

    const knowledgeTestDispatch = useKnowledgeTestDispatch();
    const knowledgeTestState = useKnowledgeTestState();

    const queryClient = useQueryClient();

    const [questions, setQuestions] = useState([]);
    const [knowledgeTestIds, setKnowledgeTestIds] = useState([]);
    const [errorIds, setErrorIds] = useState([]);
    const [isFetching, setIsFetching] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

    useEffect(() => {
        setIsFetching(true);
        getKnowledgeTestQuestions(handleGetKnowledgeTestQuestions);
        setIsFetching(false);
    }, []);

    const handleGetKnowledgeTestQuestions = (response) => {
        setQuestions(response.data.data.knowledge_questions);

        const knowledgeTestIds = response.data.data.knowledge_questions.map(item => item.id);
        setKnowledgeTestIds(knowledgeTestIds);
    };

    const handleSubmit = (values) => {
        const newErrorIds = [];

        knowledgeTestIds.forEach((id) => {
            if (!values['question_' + id]) {
                newErrorIds.push(id);
            }
        });

        setErrorIds(newErrorIds);

        if (newErrorIds.length === 0) {
            setIsLoading(true);

            const formattedValues = Object.entries(values).map(([key, value]) => {
                const questionId = parseInt(key.split('_')[1], 10);
                return {
                    question_id: questionId,
                    option: value
                };
            });

            const requestData = {
                'data': formattedValues
            };

            postSubmitKnowledgeTest(
                requestData,
                handlePostSubmitKnowledgeTest
            );

            setIsLoading(false);
        }
    };

    const handleClose = () => {
        sessionStorage.setItem('has_shown_knowledge_test_modal', true);
        knowledgeTestDispatch({ type: 'closeKnowledgeTestModal' });
    };

    const handlePostSubmitKnowledgeTest = (response) => {
        if (response.pass_knowledge_test) {
            knowledgeTestDispatch({ type: 'setPassKnowledgeTest' });
            toast.success(dict.passedKnowledgeTest, {
                duration: 5000,
            });

            queryClient.invalidateQueries(ProfileQueryKeys.GET_PROFILE_DATA);
        } else {
            knowledgeTestDispatch({ type: 'setFailKnowledgeTest' });
            toast.error(dict.failedKnowledgeTest, {
                duration: 5000,
            });
        }

        handleClose();
    };

    const renderQuestions = (questions) => {
        if (isFetching) {
            return (
                <tbody>
                    <tr>
                        <td>{dict.fetchingData}</td>
                    </tr>
                </tbody>
            );
        } else {
            return (
                <tbody>
                    <p className="font-weight-bold mb-3 mt-1 text-left">
                        {dict.knowledgeTestText1}
                    </p>
                    {questions && questions.length > 0 ? (
                        questions.map((data, index) => (
                            <tr key={index} className="d-flex align-items-center">
                                <td>
                                    <div className="align-text-left pre-investment-modal-question-div">
                                        {data[appendLocale('question')]}
                                    </div>

                                    <div className="form__form-group-field d-flex flex-column align-items-start">
                                        <div className="form__form-group-input-wrap d-flex pl-2 pb-1 pre-investment-screening-radio-div">
                                            <Field
                                                name={'question_' + data.id.toString()}
                                                type="radio"
                                                value="1"
                                            >
                                                {({ input, meta }) => (
                                                    <CustomRadioButton
                                                        input={input}
                                                        meta={meta}
                                                        label={dict.true}
                                                        className="colored"
                                                        divClassName="width-fit-content"
                                                        labelClassName="pl-2"
                                                        topLabelClassName="white-space-nowrap"
                                                    />
                                                )}
                                            </Field>

                                            <Field
                                                name={'question_' + data.id.toString()}
                                                type="radio"
                                                value="0"
                                            >
                                                {({ input, meta }) => (
                                                    <CustomRadioButton
                                                        input={input}
                                                        meta={meta}
                                                        label={dict.false}
                                                        className="colored"
                                                        divClassName="width-fit-content"
                                                        labelClassName="pl-2"
                                                        topLabelClassName="white-space-nowrap"
                                                    />
                                                )}
                                            </Field>
                                        </div>

                                        {
                                            errorIds.includes(data.id) && (
                                                <span className="validation-text">* Please select an option.</span>
                                            )
                                        }
                                    </div>
                                </td>
                            </tr>
                        ))
                    ) : null}
                </tbody>
            );
        }
    };

    const { Form } = withTypes();

    return (
        <Modal
            toggle={handleClose}
            isOpen={knowledgeTestState.showKnowledgeTestModal}
            onCancel={handleClose}
            cancelText={dict.cancel}
            submitText={dict.submit}
            submitShown
            submitBtnProps={{
                form: 'knowledgeTestForm',
                disabled: isLoading || isSubmitDisabled,
                className: 'btn rounded mb-0 width-180',
            }}
            headerType="invest"
            title={dict.knowledgeTest}
            backdrop="static"
            size="md"
        >
            <div className="modal__body pb-2">
                <Form
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                >
                    {({ dirty, handleSubmit, errors }) => (
                        <form id="knowledgeTestForm" onSubmit={handleSubmit}>
                            <Table responsive className="table--bordered">
                                {renderQuestions(questions)}
                            </Table>
                        </form>
                    )}
                </Form>

                <p className='mt-2'>
                    {dict.knowledgeTestText2}

                    <Link to={ROUTE_NAME.FAQ_GENERAL_ENQUIRIES}>
                        {dict.faqUserGuide}
                    </Link>

                    {dict.knowledgeTestText3}
                </p>
            </div>
        </Modal>
    );
};

export default KnowledgeTestModal;
